import React, { useState, useEffect } from "react";
import {
  Box, Typography, IconButton, TextField,
  FormGroup, FormControlLabel, Checkbox, FormControl, InputLabel, MenuItem, Select
} from "@mui/material";
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Label } from "recharts";
import { format, parseISO } from "date-fns";
import api from "../Api";
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  DEFAULT_GRAPH_GROUPS,
  buildGraphColorMap,
  fetchReportConfig,
} from "../appConfig";


const GraphSection = () => {
  const [graphData, setGraphData] = useState([]);
  const [days, setDays] = useState(7);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [showOrders, setShowOrders] = useState(true);
  const [showCourierReturns, setShowCourierReturns] = useState(true);
  const [showCustomerReturns, setShowCustomerReturns] = useState(true);
  const [groupBy, setGroupBy] = useState("main_account");
  const [groupOptions, setGroupOptions] = useState(DEFAULT_GRAPH_GROUPS);

  const fetchGraphData = async () => {
    try {
      const res = await api.get(`/api/orders-returns-summary?days=${days}&group_by=${groupBy}`);
      const data = res.data;
      setGraphData(data);
      const accountKeys = data.length
        ? Object.keys(data[0]).filter(k => !["date"].includes(k) && !k.includes("cour_returns") && !k.includes("cust_returns"))
        : [];
      setSelectedAccounts(accountKeys);
    } catch (err) {
      console.error("❌ Error fetching graph data:", err);
    }
  };

  useEffect(() => {
    fetchGraphData();
  }, [groupBy]);

  useEffect(() => {
    const loadReportConfig = async () => {
      const reportConfig = await fetchReportConfig();
      setGroupOptions(reportConfig.groups);
      if (!reportConfig.groups.some(group => group.value === groupBy)) {
        setGroupBy(reportConfig.groups[0]?.value || "main_account");
      }
    };
    loadReportConfig();
  }, []);

  const handleCheckbox = (account) => {
    setSelectedAccounts(prev =>
      prev.includes(account) ? prev.filter(a => a !== account) : [...prev, account]
    );
  };

  const handleSelectAll = () => {
    const allKeys = graphData.length
      ? Object.keys(graphData[0]).filter(k => !["date"].includes(k) && !k.includes("cour_returns") && !k.includes("cust_returns"))
      : [];
    setSelectedAccounts(selectedAccounts.length === allKeys.length ? [] : allKeys);
  };

  const keys = graphData.length
    ? Object.keys(graphData[0]).filter(k => !["date"].includes(k) && !k.includes("cour_returns") && !k.includes("cust_returns"))
    : [];
  const graphColorMap = buildGraphColorMap(keys);

  const getTotalReturns = (data, returnType) => {
    return data.map(row => {
      let total = 0;
      selectedAccounts.forEach(acc => {
        const key = `${acc}_${returnType}`;
        if (row[key] !== undefined) {
          total += Number(row[key] || 0);
        }
      });
      return { date: row.date, [returnType]: total };
    });
  };

  const courierReturnsData = getTotalReturns(graphData, "cour_returns");
  const customerReturnsData = getTotalReturns(graphData, "cust_returns");

  const mergedGraphData = graphData.map((row, index) => ({
    ...row,
    combined_courier_returns: courierReturnsData[index]?.cour_returns || 0,
    combined_customer_returns: customerReturnsData[index]?.cust_returns || 0,
  }));

  return (
    <Box sx={{ backgroundColor: "#f5f5f5", borderRadius: 4, p: 0 }}>
      
      {/* ✅ Controls */}
      <Box
        sx={{
          backgroundColor: "#333",
          color: "white",
          borderRadius: { xs: 2, sm: 3 },
          border: "1px solid #555",
          px: { xs: 1.25, sm: 2 },
          py: { xs: 1.5, sm: 2 },
          mx: { xs: 1, sm: 2 },
          mt: { xs: 1.5, sm: 3 },
          mb: 2,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "center" },
          justifyContent: "space-between",
          gap: { xs: 1.25, md: 1 },
          overflow: "hidden",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontFamily: "'Caveat', 'Patrick Hand', cursive",
            fontWeight: "bold",
            whiteSpace: { xs: "normal", sm: "nowrap" },
            fontSize: { xs: "1.35rem", sm: "1.5rem" },
            lineHeight: 1.15,
          }}
        >
          📊 Orders + Returns Graph
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "minmax(0, 1fr) 82px 38px", sm: "minmax(220px, 1fr) 100px 40px" },
            alignItems: "center",
            gap: 1,
            width: { xs: "100%", md: "auto" },
            minWidth: 0,
          }}
        >
          <FormControl size="small" sx={{ minWidth: 0, width: "100%" }}>
            <InputLabel sx={{ color: "white" }}>Group</InputLabel>
            <Select
              label="Group"
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              sx={{
                width: "100%",
                color: "white",
                ".MuiOutlinedInput-notchedOutline": { borderColor: "white" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#bbb" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#bbb" },
                ".MuiSvgIcon-root": { color: "white" },
              }}
            >
              {groupOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            type="number"
            label="Days"
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value) || 1)}
            size="small"
            sx={{
              width: "100%",
              input: { color: "white" },
              label: { color: "white" },
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: "white" },
                "&:hover fieldset": { borderColor: "#bbb" },
                "&.Mui-focused fieldset": { borderColor: "#bbb" },
              },
            }}
          />
          <IconButton onClick={fetchGraphData} sx={{ color: "white" }}>
            <RefreshIcon />
          </IconButton>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(3, auto)" },
            alignItems: "center",
            columnGap: { xs: 1, sm: 2 },
            rowGap: 0.5,
            width: { xs: "100%", md: "auto" },
          }}
        >
          <FormControlLabel
            control={<Checkbox checked={showOrders} onChange={() => setShowOrders(prev => !prev)} sx={{ color: "white" }} />}
            label="Orders"
            sx={{ color: "white", m: 0, minWidth: 0, "& .MuiFormControlLabel-label": { fontSize: { xs: "0.95rem", sm: "1rem" }, lineHeight: 1.15 } }}
          />
          <FormControlLabel
            control={<Checkbox checked={showCourierReturns} onChange={() => setShowCourierReturns(prev => !prev)} sx={{ color: "white" }} />}
            label="Courier Returns"
            sx={{ color: "white", m: 0, minWidth: 0, "& .MuiFormControlLabel-label": { fontSize: { xs: "0.95rem", sm: "1rem" }, lineHeight: 1.15 } }}
          />
          <FormControlLabel
            control={<Checkbox checked={showCustomerReturns} onChange={() => setShowCustomerReturns(prev => !prev)} sx={{ color: "white" }} />}
            label="Customer Returns"
            sx={{ color: "white", m: 0, minWidth: 0, "& .MuiFormControlLabel-label": { fontSize: { xs: "0.95rem", sm: "1rem" }, lineHeight: 1.15 } }}
          />
        </Box>
      </Box>

      {/* ✅ Graph */}
      <Box sx={{ px: 2, pt: 0, pb: 0, m: 0 }}>
        <ResponsiveContainer width="100%" height={window.innerHeight * 0.55}>
          {graphData.length > 0 ? (
            <ComposedChart data={mergedGraphData} margin={{ top: 20, right: 30, left: 20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={(d) => format(parseISO(d), "dd MMM")}
                angle={-45}
                textAnchor="end"
                interval={0}
                height={60}
              />
              <YAxis allowDecimals={false}>
                <Label value="Quantity" angle={-90} position="insideLeft" />
              </YAxis>

              {/* ✅ Custom Tooltip */}
              <Tooltip content={({ payload, label }) => {
                if (!payload || payload.length === 0) return null;

                const currentRow = graphData.find(row => row.date === label);
                if (!currentRow) return null;

                let totalOrders = 0;
                let totalCourierReturns = 0;
                let totalCustomerReturns = 0;

                selectedAccounts.forEach(acc => {
                  totalOrders += Number(currentRow[acc] || 0);
                  totalCourierReturns += Number(currentRow[`${acc}_cour_returns`] || 0);
                  totalCustomerReturns += Number(currentRow[`${acc}_cust_returns`] || 0);
                });

                return (
                  <Box sx={{ backgroundColor: "#fff", border: "1px solid #ccc", p: 2, minWidth: 220 }}>
                    {/* 📅 Date */}
                    <Typography sx={{ fontWeight: "bold", mb: 1 }}>
                      {format(parseISO(label), "dd MMMM")}
                    </Typography>

                    {/* Totals */}
                    <Typography sx={{ fontWeight: "bold", color: "#000", mb: 0.5 }}>
                      Orders: {totalOrders}
                    </Typography>
                    <Typography sx={{ fontWeight: "bold", color: "blue", mb: 0.5 }}>
                      Cour. Rtrn: {totalCourierReturns}
                    </Typography>
                    <Typography sx={{ fontWeight: "bold", color: "red", mb: 1 }}>
                      Cust. Rtrn: {totalCustomerReturns}
                    </Typography>

                    {/* Per account details */}
                    {selectedAccounts.map((acc, idx) => {
                      const orders = currentRow[acc] || 0;
                      const courierReturns = currentRow[`${acc}_cour_returns`] || 0;
                      const customerReturns = currentRow[`${acc}_cust_returns`] || 0;

                      return (
                        <Typography key={idx} sx={{ fontWeight: "bold", color: graphColorMap[acc] || "#111827" }}>
                          {acc}: 
                          <span style={{ color: "black", fontWeight: "bold" }}> {orders}</span>
                          <span style={{ color: "blue", fontWeight: "bold" }}>-{courierReturns}</span>
                          <span style={{ color: "red", fontWeight: "bold" }}>-{customerReturns}</span>
                        </Typography>
                      );
                    })}
                  </Box>
                );
              }}/>


              {/* ✅ Orders Bars */}
              {showOrders && keys.map((k) => (
                selectedAccounts.includes(k) && (
                  <Bar
                    key={k}
                    dataKey={k}
                    stackId="orders"
                    fill={graphColorMap[k]}
                    barSize={30}
                  />
                )
              ))}

              {/* ✅ Lines */}
              {showCourierReturns && (
                <Line
                  type="monotone"
                  dataKey="combined_courier_returns"
                  stroke="blue"
                  strokeWidth={3}
                  dot={{ r: 1 }}
                  activeDot={{ r: 6 }}
                  name="Courier Returns"
                />
              )}
              {showCustomerReturns && (
                <Line
                  type="monotone"
                  dataKey="combined_customer_returns"
                  stroke="red"
                  strokeWidth={3}
                  dot={{ r: 1 }}
                  activeDot={{ r: 6 }}
                  name="Customer Returns"
                />
              )}
            </ComposedChart>
          ) : (
            <Box sx={{ height: window.innerHeight * 0.55, display: "flex", justifyContent: "center", alignItems: "center", fontSize: "1.5rem", color: "#777" }}>
              No Data Found
            </Box>
          )}
        </ResponsiveContainer>
      </Box>

      {/* ✅ Account Selector */}
      <Box sx={{ px: 2, pt: 0, pb: 2, m: 0 }}>
        <FormGroup row>
          <FormControlLabel
            control={<Checkbox checked={selectedAccounts.length === keys.length} onChange={handleSelectAll} sx={{ color: "#333", '&.Mui-checked': { color: "#00e676" } }} />}
            label="All"
          />
          {keys.map((k) => (
            <FormControlLabel
              key={k}
              control={<Checkbox checked={selectedAccounts.includes(k)} onChange={() => handleCheckbox(k)} sx={{ color: graphColorMap[k], '&.Mui-checked': { color: graphColorMap[k] } }} />}
              label={k}
            />
          ))}
        </FormGroup>
      </Box>

    </Box>
  );
};

export default GraphSection;
